import { useEffect, useRef, useState } from 'preact/hooks';
import {
  activeChatId,
  chatDisplayName,
  chats,
  deliveryState,
  editMessage,
  messages,
  pending,
  sendMessage,
  session,
} from '../lib/engine.js';
import { decodeBody, type ReplyReference } from '../lib/message-body.js';
import { back, go } from './nav.js';
import { Avatar } from './Avatar.js';
import { ConnectionBar } from './ConnectionBar.js';
import { Ticks } from './Ticks.js';
import { useEdgeSwipeBack } from './useEdgeSwipeBack.js';
import { useMessageGestures } from './useMessageGestures.js';
import type { StoredChat, StoredMessage } from '../lib/store.js';

/** Что сейчас делает поле ввода: пишет новое или правит существующее. */
type Composing =
  | { mode: 'new'; replyTo?: ReplyReference }
  | { mode: 'edit'; messageId: string; original: string };

export function ChatView() {
  const chatId = activeChatId.value!;
  const chat = chats.value.find((row) => row.id === chatId);
  const [draft, setDraft] = useState('');
  const [composing, setComposing] = useState<Composing>({ mode: 'new' });
  const [menuFor, setMenuFor] = useState<StoredMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const edge = useEdgeSwipeBack(back);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages.value.length, pending.value.length, chatId]);

  /**
   * Клавиатура открылась — доводим ленту до последнего сообщения.
   *
   * Видимая область при этом уменьшается, но положение прокрутки остаётся
   * прежним, и последние сообщения оказываются под клавиатурой. Разметку
   * ради этого трогать не надо: достаточно домотать ленту.
   */
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const toBottom = () => bottom.current?.scrollIntoView({ block: 'end' });
    viewport.addEventListener('resize', toBottom);
    return () => viewport.removeEventListener('resize', toBottom);
  }, []);

  /**
   * Поле ввода растёт под текст.
   *
   * Сбросить высоту в auto перед замером обязательно: иначе scrollHeight
   * считается от уже растянутого поля, и при удалении текста оно так и
   * останется высоким. Потолок задан в CSS через max-height, отсюда высота
   * ставится без оглядки на него — дальше сработает прокрутка внутри поля.
   *
   * Рамки прибавляются отдельно. box-sizing здесь border-box, то есть высота
   * включает рамки, а scrollHeight — нет. Поставить одно в другое напрямую
   * значит навсегда оставить поле на два пикселя ниже нужного: содержимое
   * не влезает, и браузер рисует полосу прокрутки даже на пустом поле.
   */
  useEffect(() => {
    const field = input.current;
    if (!field) return;
    field.style.height = 'auto';
    const borders = field.offsetHeight - field.clientHeight;
    field.style.height = `${field.scrollHeight + borders}px`;
  }, [draft, composing.mode]);

  if (!chat) return null;

  const startReply = (message: StoredMessage) => {
    const author =
      message.senderId === session.value?.userId
        ? 'Вы'
        : (chat.members.find((member) => member.id === message.senderId)?.displayName ?? '');

    setComposing({
      mode: 'new',
      replyTo: {
        id: message.id,
        author,
        preview: decodeBody(message.plaintext ?? '').text,
      },
    });
    input.current?.focus();
  };

  const startEdit = (message: StoredMessage) => {
    const text = decodeBody(message.plaintext ?? '').text;
    setComposing({ mode: 'edit', messageId: message.id, original: text });
    setDraft(text);
    input.current?.focus();
  };

  const cancel = () => {
    if (composing.mode === 'edit') setDraft('');
    setComposing({ mode: 'new' });
  };

  const submit = async (event: Event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;

    const previous = composing;
    setDraft('');
    setComposing({ mode: 'new' });
    setError(null);

    try {
      if (previous.mode === 'edit') {
        // Текст не изменился — правка не нужна, лишний запрос и отметка
        // «изменено» на ровном месте только запутают собеседника.
        if (text !== previous.original) await editMessage(chatId, previous.messageId, text);
      } else {
        await sendMessage(chatId, text, previous.replyTo);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Не удалось отправить');
      setDraft(text);
      setComposing(previous);
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && composing.mode !== 'new') {
      event.preventDefault();
      cancel();
      return;
    }
    // Enter отправляет, Shift+Enter переносит строку — но только на десктопе:
    // на телефоне Enter должен переносить строку, там отправляют кнопкой.
    const isDesktop = !matchMedia('(pointer: coarse)').matches;
    if (isDesktop && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submit(event);
    }
  };

  const members = chat.kind === 'group' ? `${chat.members.length} участников` : null;
  const other = chat.members.find((member) => member.id !== session.value?.userId);

  return (
    <div
      class="screen"
      style={edge.shift > 0 ? `transform: translateX(${edge.shift}px)` : undefined}
      {...edge.handlers}
    >
      <header class="topbar">
        <button class="icon-btn" onClick={back} aria-label="Назад">
          ‹
        </button>
        {chat.kind === 'direct' && other && (
          <Avatar userId={other.id} name={chatDisplayName(chat)} size={32} />
        )}
        <h1>
          {chatDisplayName(chat)}
          {members && <span class="sub">{members}</span>}
        </h1>
        <button class="icon-btn" onClick={() => go('chat-info')} aria-label="О чате">
          ⋯
        </button>
      </header>

      <ConnectionBar />

      <div class="scroll">
        <div class="messages">
          {messages.value.length === 0 && pending.value.length === 0 && (
            <div class="empty">Здесь пока ничего нет. Напиши первым.</div>
          )}

          {withDaySeparators(messages.value).map((entry) =>
            entry.kind === 'day' ? (
              <div class="day-sep" key={`day-${entry.label}`}>
                {entry.label}
              </div>
            ) : (
              <Bubble
                key={entry.message.id}
                message={entry.message}
                chat={chat}
                onReply={() => startReply(entry.message)}
                onEdit={() => startEdit(entry.message)}
                onLongPress={() => setMenuFor(entry.message)}
              />
            ),
          )}

          {pending.value.map((item) => (
            <div class="bubble mine pending" key={item.localId}>
              {decodeBody(item.plaintext).text}
              <span class="meta">
                <Ticks state="pending" />
              </span>
            </div>
          ))}

          <div ref={bottom} />
        </div>
      </div>

      {/*
        Меню по долгому нажатию. На телефоне это единственный способ добраться
        до правки: кнопки над пузырём показываются только по наведению мыши.
      */}
      {menuFor && (
        <div class="sheet-backdrop" onClick={() => setMenuFor(null)}>
          <div class="sheet" onClick={(event) => event.stopPropagation()}>
            <button
              class="sheet-item"
              onClick={() => {
                startReply(menuFor);
                setMenuFor(null);
              }}
            >
              ↩ Ответить
            </button>
            {menuFor.senderId === session.value?.userId && menuFor.plaintext !== null && (
              <button
                class="sheet-item"
                onClick={() => {
                  startEdit(menuFor);
                  setMenuFor(null);
                }}
              >
                ✎ Изменить
              </button>
            )}
            <button
              class="sheet-item"
              onClick={() => {
                if (menuFor.plaintext !== null) {
                  void navigator.clipboard?.writeText(decodeBody(menuFor.plaintext).text);
                }
                setMenuFor(null);
              }}
            >
              ⧉ Скопировать
            </button>
            <button class="sheet-item cancel" onClick={() => setMenuFor(null)}>
              Отмена
            </button>
          </div>
        </div>
      )}

      {error && (
        <div class="error" style="margin: 0 12px 8px">
          {error}
        </div>
      )}

      {composing.mode === 'edit' && (
        <div class="compose-hint">
          <div class="body">
            <div class="label">Изменение сообщения</div>
            <div class="text">{composing.original}</div>
          </div>
          <button class="icon-btn" onClick={cancel} aria-label="Отменить">
            ✕
          </button>
        </div>
      )}

      {composing.mode === 'new' && composing.replyTo && (
        <div class="compose-hint">
          <div class="body">
            <div class="label">Ответ · {composing.replyTo.author}</div>
            <div class="text">{composing.replyTo.preview}</div>
          </div>
          <button class="icon-btn" onClick={cancel} aria-label="Отменить">
            ✕
          </button>
        </div>
      )}

      <form class="composer" onSubmit={submit}>
        <textarea
          ref={input}
          value={draft}
          onInput={(event) => setDraft((event.target as HTMLTextAreaElement).value)}
          onKeyDown={onKeyDown}
          onFocus={() => {
            // Клавиатура выезжает с анимацией: мгновенная прокрутка ничего
            // не даёт, лента к этому моменту ещё прежней высоты.
            setTimeout(() => bottom.current?.scrollIntoView({ block: 'end' }), 250);
          }}
          placeholder={composing.mode === 'edit' ? 'Новый текст' : 'Сообщение'}
          rows={1}
          maxLength={8000}
        />
        <button class="btn" type="submit" disabled={!draft.trim()}>
          {composing.mode === 'edit' ? '✓' : '→'}
        </button>
      </form>
    </div>
  );
}

function Bubble({
  message,
  chat,
  onReply,
  onEdit,
  onLongPress,
}: {
  message: StoredMessage;
  chat: StoredChat;
  onReply: () => void;
  onEdit: () => void;
  onLongPress: () => void;
}) {
  const mine = message.senderId === session.value?.userId;
  const { shift, handlers } = useMessageGestures(onReply, onLongPress);

  if (message.plaintext === null) {
    return <div class="bubble unreadable">Сообщение не расшифровано — нет ключа этой эпохи</div>;
  }

  const body = decodeBody(message.plaintext);
  const author =
    chat.kind === 'group' && !mine
      ? (chat.members.find((member) => member.id === message.senderId)?.displayName ?? null)
      : null;

  return (
    <div
      class={`bubble ${mine ? 'mine' : ''}`}
      style={shift > 0 ? `transform: translateX(${shift}px)` : undefined}
      {...handlers}
    >
      {body.replyTo && (
        <div class="quote">
          <div class="quote-author">{body.replyTo.author}</div>
          <div class="quote-text">{body.replyTo.preview}</div>
        </div>
      )}

      {author && <span class="author">{author}</span>}
      {body.text}

      <span class="meta">
        {message.editedAt !== null && <span class="edited">изменено</span>}
        <span class="time">
          {new Date(message.createdAt).toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
        {mine && <Ticks state={deliveryState(chat, message)} />}
      </span>

      {/* Кнопки для мыши: свайпа на десктопе нет, а цитировать и править надо. */}
      <div class="bubble-actions">
        <button class="icon-btn" onClick={onReply} title="Ответить" aria-label="Ответить">
          ↩
        </button>
        {mine && (
          <button class="icon-btn" onClick={onEdit} title="Изменить" aria-label="Изменить">
            ✎
          </button>
        )}
      </div>

      {/*
        Кнопка меню на сенсорных экранах — видна всегда.

        Жесты там оказались ненадёжны: система по-разному трактует двойное
        касание, и меню то открывалось, то нет. Действия над сообщением
        не должны зависеть от того, угадал ли браузер намерение пальца,
        поэтому здесь простая кнопка. Свайп и двойное касание остались
        как быстрый путь для тех, у кого они работают.
      */}
      <button class="bubble-menu" onClick={onLongPress} aria-label="Действия с сообщением">
        ⋯
      </button>
    </div>
  );
}

type Entry = { kind: 'day'; label: string } | { kind: 'message'; message: StoredMessage };

/** Вставляет разделители дат между сообщениями разных суток. */
function withDaySeparators(list: StoredMessage[]): Entry[] {
  const entries: Entry[] = [];
  let lastDay = '';

  for (const message of list) {
    const day = new Date(message.createdAt).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
    });
    if (day !== lastDay) {
      entries.push({ kind: 'day', label: day });
      lastDay = day;
    }
    entries.push({ kind: 'message', message });
  }
  return entries;
}
